import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import EventIcon from '@mui/icons-material/Event';
import {
  Box,
  InputAdornment,
  List,
  ListItemButton,
  ListItemText,
  Popover,
  TextField,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import OverlineLabel from 'components/common/OverlineLabel';
import moment from 'moment';
import { bool, func, object } from 'prop-types';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { I18n } from 'utils/i18n';

const START_PRESETS = [
  {
    id: 'today',
    labelKey: 'InitiativeSchedulePicker.Today',
    resolve: () => moment().startOf('day'),
  },
  {
    id: 'tomorrow',
    labelKey: 'InitiativeSchedulePicker.Tomorrow',
    resolve: () => moment().add(1, 'day').startOf('day'),
  },
  {
    id: 'nextMonday',
    labelKey: 'InitiativeSchedulePicker.Next Monday',
    resolve: () => moment().add(1, 'week').startOf('isoWeek'),
  },
  {
    id: 'nextMonth',
    labelKey: 'InitiativeSchedulePicker.Next Month',
    resolve: () => moment().add(1, 'month').startOf('month'),
  },
];

const END_PRESETS = [
  { id: 'none', labelKey: 'InitiativeSchedulePicker.Indefinite' },
  { id: '7d', labelKey: 'InitiativeSchedulePicker.Days 7', offset: [7, 'days'] },
  { id: '14d', labelKey: 'InitiativeSchedulePicker.Days 14', offset: [14, 'days'] },
  { id: '30d', labelKey: 'InitiativeSchedulePicker.Days 30', offset: [30, 'days'] },
  { id: '3m', labelKey: 'InitiativeSchedulePicker.Months 3', offset: [3, 'months'] },
];

const toISO = m => (m ? m.toISOString() : null);

const formatDate = iso => (iso ? moment(iso).format('D MMM, YYYY') : '');

const dateInputSx = {
  flex: 1,
  cursor: 'pointer',
  '& input': {
    cursor: 'pointer',
    fontSize: 13,
    fontFamily: 'monospace',
  },
};

const InitiativeSchedulePicker = ({ value = null, onChange, allowPastStartDate = false }) => {
  const startRef = useRef(null);
  const endRef = useRef(null);

  const [activeField, setActiveField] = useState(null);
  const anchorEl =
    activeField === 'start' ? startRef.current : activeField === 'end' ? endRef.current : null;

  const { startPreset, startDate, endPreset, endDate } = value || {};

  const resolvedStartDate = useMemo(() => {
    if (startPreset) {
      const preset = START_PRESETS.find(p => p.id === startPreset);
      return preset?.resolve ? toISO(preset.resolve()) : startDate;
    }
    return startDate;
  }, [startPreset, startDate]);

  const resolvedEndDate = useMemo(() => {
    if (endPreset) {
      const preset = END_PRESETS.find(p => p.id === endPreset);
      if (!preset) return endDate;
      if (preset.id === 'none') return null;
      if (preset.offset && resolvedStartDate) {
        return toISO(
          moment(resolvedStartDate)
            .add(...preset.offset)
            .endOf('day'),
        );
      }
    }
    return endDate;
  }, [endPreset, endDate, resolvedStartDate]);

  const handleStartPreset = useCallback(
    presetId => {
      const preset = START_PRESETS.find(p => p.id === presetId);
      const newStartDate = preset?.resolve ? toISO(preset.resolve()) : startDate;
      const updatedValue = { ...value, startPreset: presetId, startDate: newStartDate };

      if (value?.endPreset) {
        const endDef = END_PRESETS.find(p => p.id === value.endPreset);
        if (endDef?.offset && newStartDate) {
          updatedValue.endDate = toISO(
            moment(newStartDate)
              .add(...endDef.offset)
              .endOf('day'),
          );
        }
      }
      onChange(updatedValue);
      setActiveField(null);
    },
    [value, onChange, startDate],
  );

  const handleStartCalendar = useCallback(
    momentDate => {
      if (!momentDate?.isValid()) return;
      const newStartDate = toISO(momentDate.startOf('day'));
      const updatedValue = { ...value, startPreset: null, startDate: newStartDate };

      if (value?.endPreset) {
        const endDef = END_PRESETS.find(p => p.id === value.endPreset);
        if (endDef?.offset) {
          updatedValue.endDate = toISO(
            moment(newStartDate)
              .add(...endDef.offset)
              .endOf('day'),
          );
        }
      }
      onChange(updatedValue);
      setActiveField(null);
    },
    [value, onChange],
  );

  const handleEndPreset = useCallback(
    presetId => {
      const preset = END_PRESETS.find(p => p.id === presetId);
      const currentStart = resolvedStartDate || new Date().toISOString();
      let newEndDate = null;
      if (preset?.offset) {
        newEndDate = toISO(
          moment(currentStart)
            .add(...preset.offset)
            .endOf('day'),
        );
      }
      onChange({ ...value, endPreset: presetId, endDate: newEndDate });
      setActiveField(null);
    },
    [value, onChange, resolvedStartDate],
  );

  const handleEndCalendar = useCallback(
    momentDate => {
      if (!momentDate?.isValid()) return;
      onChange({ ...value, endPreset: null, endDate: toISO(momentDate.endOf('day')) });
      setActiveField(null);
    },
    [value, onChange],
  );

  const isStartPopover = activeField === 'start';
  const presets = isStartPopover ? START_PRESETS : END_PRESETS;
  const activePreset = isStartPopover ? startPreset : endPreset;
  const calendarValue = isStartPopover
    ? resolvedStartDate
      ? moment(resolvedStartDate)
      : null
    : resolvedEndDate
      ? moment(resolvedEndDate)
      : null;
  const calendarMinDate = isStartPopover
    ? allowPastStartDate
      ? undefined
      : moment()
    : resolvedStartDate
      ? moment(resolvedStartDate)
      : moment();
  const onPresetClick = isStartPopover ? handleStartPreset : handleEndPreset;
  const onCalendarChange = isStartPopover ? handleStartCalendar : handleEndCalendar;

  return (
    <div>
      <OverlineLabel>{I18n.t('InitiativeSchedulePicker.Duration')}</OverlineLabel>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <TextField
          ref={startRef}
          size="small"
          fullWidth
          value={formatDate(resolvedStartDate)}
          placeholder={I18n.t('InitiativeSchedulePicker.Start placeholder')}
          onClick={() => setActiveField('start')}
          slotProps={{
            input: {
              readOnly: true,
              startAdornment: (
                <InputAdornment position="start">
                  <CalendarTodayIcon
                    sx={{
                      fontSize: 18,
                      color: activeField === 'start' ? 'primary.main' : 'text.secondary',
                    }}
                  />
                </InputAdornment>
              ),
            },
          }}
          sx={
            activeField === 'start'
              ? {
                  ...dateInputSx,
                  '& .MuiOutlinedInput-root': {
                    borderColor: 'primary.main',
                    '& fieldset': { borderColor: 'primary.main' },
                  },
                }
              : dateInputSx
          }
        />
        <Typography
          sx={{ color: 'text.secondary', flexShrink: 0, fontSize: 14, userSelect: 'none' }}
        >
          →
        </Typography>
        <TextField
          ref={endRef}
          size="small"
          fullWidth
          value={
            endPreset === 'none'
              ? I18n.t('InitiativeSchedulePicker.No end')
              : formatDate(resolvedEndDate)
          }
          placeholder={I18n.t('InitiativeSchedulePicker.End placeholder')}
          onClick={() => setActiveField('end')}
          slotProps={{
            input: {
              readOnly: true,
              startAdornment: (
                <InputAdornment position="start">
                  <EventIcon
                    sx={{
                      fontSize: 18,
                      color: activeField === 'end' ? 'primary.main' : 'text.secondary',
                    }}
                  />
                </InputAdornment>
              ),
            },
          }}
          sx={
            activeField === 'end'
              ? {
                  ...dateInputSx,
                  '& .MuiOutlinedInput-root': {
                    borderColor: 'primary.main',
                    '& fieldset': { borderColor: 'primary.main' },
                  },
                }
              : dateInputSx
          }
        />
      </Box>

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setActiveField(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{
          paper: {
            sx: theme => ({
              display: 'flex',
              overflow: 'hidden',
              borderRadius: '8px',
              backgroundColor: theme.palette.background.paper,
            }),
          },
        }}
        sx={{ mt: 1 }}
      >
        <List
          disablePadding
          sx={theme => ({
            width: 160,
            borderRight: `1px solid ${theme.palette.divider}`,
            backgroundColor:
              theme.palette.mode === 'dark'
                ? theme.palette.background.paper
                : theme.palette.grey[50],
            p: 0.5,
          })}
        >
          {presets.map(preset => (
            <ListItemButton
              key={preset.id}
              selected={activePreset === preset.id}
              onClick={() => onPresetClick(preset.id)}
              dense
              sx={theme => ({
                borderRadius: '4px',
                py: 0.75,
                px: 1.5,
                minHeight: 'auto',
                color: theme.palette.text.primary,
                '&:hover': {
                  backgroundColor: theme.palette.action.hover,
                },
                '&.Mui-selected': {
                  backgroundColor: alpha(theme.palette.primary.main, 0.1),
                  color: theme.palette.primary.main,
                  fontWeight: 500,
                },
              })}
            >
              <ListItemText
                sx={{
                  borderBottom: 'none',
                  minHeight: 'auto',
                  p: 0,
                  '& p': {
                    color: 'inherit',
                  },
                }}
                primary={I18n.t(preset.labelKey)}
                primaryTypographyProps={{ variant: 'body2' }}
              />
            </ListItemButton>
          ))}
        </List>
        <Box sx={{ p: 1 }}>
          <DateCalendar
            value={calendarValue}
            onChange={onCalendarChange}
            minDate={calendarMinDate}
          />
        </Box>
      </Popover>
    </div>
  );
};

InitiativeSchedulePicker.propTypes = {
  value: object,
  onChange: func.isRequired,
  allowPastStartDate: bool,
};

export default InitiativeSchedulePicker;
